import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import isArchived from 'archive/isArchived';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import {
  getRestrictionAccessDisplayText,
  getCICOStatusText,
  getCICOWarningText,
  getSampleStatusTooltip,
  doesCurrentUserHaveAccessForItem,
  isItemAvailable,
  isItemAvailableToCurrentUser,
  doesItemHaveBlockingStatusWarning,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import * as Colors from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface StatusLabelComponentProps {
  className?: string;
  isWarning: boolean;
  isBlocking: boolean;
  showCheckmark?: boolean;
  message: string | null;
}

export function shouldShowWarning(
  item: SampleStatusItem,
  includeAuthorization: boolean,
  includeAvailability: boolean,
  includeExpiration: boolean = false
): boolean {
  const showUnauthorizedWarning = includeAuthorization && !doesCurrentUserHaveAccessForItem(item);
  const showUnavailableWarning = includeAvailability && !isItemAvailableToCurrentUser(item);
  const showExpiredWarning = includeExpiration && item.expirationInfo?.isExpired;
  return showUnauthorizedWarning || showUnavailableWarning || Boolean(showExpiredWarning);
}

export function StatusLabelComponent(props: StatusLabelComponentProps): JSX.Element {
  const getIcon = (): JSX.Element | null => {
    if (props.isBlocking) {
      return (
        <BenchlingIcon
          className={classNames(css(styles.colorRedDark), {'u-mini-spacing-right': props.message != null})}
          icon="revision"
        />
      );
    }
    if (props.isWarning) {
      return (
        <BenchlingIcon
          className={classNames(css(styles.colorOrange), {'u-mini-spacing-right': props.message != null})}
          icon="warning"
        />
      );
    }
    if (props.showCheckmark) {
      return (
        <BenchlingIcon
          className={classNames(css(styles.colorGreen), {'u-mini-spacing-right': props.message != null})}
          icon="done"
        />
      );
    }
    return null;
  };

  return (
    <span className={props.className} {...getTestProps({component: 'SampleStatusLabel', element: 'root'})}>
      {getIcon()}
      {props.message}
    </span>
  );
}

export type RenderAllStatusesProps =
  | {
      // true if "Available" and "Authorized" labels should be rendered
      renderAllStatuses: true;
      // If renderAllStatuses is true, we need to know whether the item supports sample status (CICO), so we
      // we show 'N/A' instead of 'Available'. Currently, this should only be true for containers that are
      // not well plate wells
      itemSupportsSampleStatus: boolean;
    }
  | {
      renderAllStatuses: false;
      itemSupportsSampleStatus?: undefined;
    };

interface BaseProps {
  className?: string;
  item: SampleStatusItem | null;
  includeAuthorization: boolean;
  includeAvailability: boolean;
  includeExpiration?: boolean;
  // Hide assignee in the label and instead include in the tooltip.
  hideAssigneeInLabel?: boolean;
  // When this is false, will show warning icon with tooltip only. Tooltip will include assignee, if applicable
  includeMessage?: boolean;
}

export type SampleStatusLabelProps = BaseProps & RenderAllStatusesProps;

export default function SampleStatusLabel({
  className,
  item,
  includeAuthorization,
  includeAvailability,
  includeExpiration,
  hideAssigneeInLabel = false,
  includeMessage = true,
  renderAllStatuses,
  itemSupportsSampleStatus,
}: SampleStatusLabelProps): JSX.Element | null {
  if (item == null || (isArchived({...item, name: ''}) && !renderAllStatuses)) {
    return null;
  } else if (isArchived({...item, name: ''})) {
    return (
      <StatusLabelComponent
        className={className}
        isWarning={false}
        isBlocking={false}
        showCheckmark={false}
        message="N/A"
      />
    );
  }

  const getStatusText = (): string => {
    const showAuthStatus =
      includeAuthorization && (renderAllStatuses || !doesCurrentUserHaveAccessForItem(item));
    const showAvailabilityStatus = includeAvailability && (renderAllStatuses || !isItemAvailable(item));
    const showExpiration = includeExpiration && (renderAllStatuses || item?.expirationInfo?.isExpired);
    const authorizationStatus = showAuthStatus ? getRestrictionAccessDisplayText(item) : undefined;
    const expirationStatus = showExpiration
      ? item?.expirationInfo?.isExpired
        ? 'Expired'
        : 'Not expired'
      : undefined;
    let availabilityStatus;
    if (!showAvailabilityStatus) {
      availabilityStatus = undefined;
    } else if (!renderAllStatuses) {
      // The item must be unavailable
      availabilityStatus = getCICOWarningText(item.checkOutStatusInfo!, {
        hideAssignee: hideAssigneeInLabel,
      });
    } else {
      availabilityStatus = getCICOStatusText(item, Boolean(itemSupportsSampleStatus), {
        hideAssignee: hideAssigneeInLabel,
      });
    }

    return [authorizationStatus, expirationStatus, availabilityStatus]
      .filter((status) => status != null)
      .join(', ');
  };

  const tooltipText = getSampleStatusTooltip(item, {
    includeAvailability,
    includeAuthorization,
    hideAssignee: includeMessage && !hideAssigneeInLabel,
    includeExpiration,
  });
  return (
    <Tooltip text={tooltipText} isDisabled={!tooltipText}>
      <StatusLabelComponent
        className={className}
        isWarning={shouldShowWarning(item, includeAuthorization, includeAvailability, includeExpiration)}
        isBlocking={includeAuthorization && doesItemHaveBlockingStatusWarning(item)}
        showCheckmark={item.checkOutStatusInfo?.isAssignedToCurrentUser && includeAvailability}
        message={includeMessage ? getStatusText() : null}
      />
    </Tooltip>
  );
}

export const styles = StyleSheet.create({
  colorOrange: {
    color: Colors.ORANGE,
  },
  colorGrayDark: {
    color: Colors.GRAY_DARK,
  },
  colorGreen: {
    color: Colors.GREEN,
  },
  colorRedDark: {
    color: Colors.RED_DARK,
  },
});
